import {StopType} from "../../common/types";
import {
  EtaItemInput,
  GetRoutePlanningResultResponse,
  Job,
  ManifestStop,
  NestedJobStop,
  RoutePlanningActivity,
  RoutePlanningDriverInput,
  RoutePlanningRouteEtas,
  RoutePlanningSolution,
  SubmitRoutePlanningInput
} from "../../../generated/graphql";
import {
  isActiveOrQueued,
  isDeliveryOrBothStop,
  isPickupStop,
  isSupportedStopStatus,
  sortStopsByScheduledAndSequence
} from "../../../utils/stops/StopUtils";
import {RowData} from "../types/types";
import log from "loglevel";
import {ETASourceType} from "../../../constants/constants";
import {AssignmentDetailWithEndDateTime, RoutePlanningDriver} from "../../../providers/RoutePlanningProvider";
import {parseRouteWithEtasResult} from "../GetRouteWithEtas.service";
import {RouteCostTotals} from "../RouteCost.service";
import {combineDatesAndTime} from "../../../utils/DayjsUtils";

/**
 * Determines the time window (leftbound and rightbound) for a stop.
 *
 * For Delivery (D) and Both (B) stops,
 * - The Leftbound follows this priority:
 *  1. Early time of the delivery/both stop, if provided
 *  2. The early time of the latest pickup stop on the job (by scheduledDateTime, Sequence)
 *  3. The scheduled time of the latest pickup stop on the job (by scheduledDateTime, Sequence)
 *  4. Epoch (fallback)
 * - Rightbound: Late time → Scheduled time → Null
 *
 * For Pickup (P) stops:
 * - Leftbound: Early time → Scheduled time → Epoch
 * - The Rightbound follows this priority:
 *  1. Late time
 *  2. If Leftbound is not equal to the Epoch, Leftbound + 4 hours
 *  3. Scheduled time of the first Delivery or Both stop (by scheduledDateTime, Sequence)
 *  4. Late time of the first Delivery or Both stop (by scheduledDateTime, Sequence)
 *  5. Null
 *
 */
export const getStopTimeWindow = (stop: NestedJobStop, job: Job): [Date | null, Date | null] => {
  const epoch = new Date(0);
  const FOUR_HOURS_MS = 4 * 60 * 60 * 1000;

  let leftbound: Date | null = null;
  let rightbound: Date | null = null;

  if (stop.stopType === StopType.PICKUP) {
    if (stop.earlyDateTime) {
      leftbound = new Date(stop.earlyDateTime);
    } else if (stop.scheduledDateTime) {
      leftbound = new Date(stop.scheduledDateTime);
    } else {
      leftbound = epoch;
    }

    if (stop.lateDateTime) {
      rightbound = new Date(stop.lateDateTime);
    } else if (leftbound.getTime() > 0) {
      rightbound = new Date(leftbound.getTime() + FOUR_HOURS_MS);
    } else {
      const deliveryStops = job.stops.filter((s) =>
        [StopType.DELIVERY, StopType.BOTH].includes(s.stopType as StopType)
      );
      if (deliveryStops.length > 0) {
        const firstDelivery = sortStopsByScheduledAndSequence(deliveryStops)[0];
        if (firstDelivery.scheduledDateTime) {
          rightbound = new Date(firstDelivery.scheduledDateTime);
        } else if (firstDelivery.lateDateTime) {
          rightbound = new Date(firstDelivery.lateDateTime);
        }
      }
    }
  } else {
    if (stop.earlyDateTime) {
      leftbound = new Date(stop.earlyDateTime);
    } else {
      const pickupStops = job.stops.filter((s) => s.stopType === StopType.PICKUP);
      if (pickupStops.length > 0) {
        const sortedPickup = sortStopsByScheduledAndSequence(pickupStops, "desc");
        const latestPickup = sortedPickup[0];
        if (latestPickup.earlyDateTime) {
          leftbound = new Date(latestPickup.earlyDateTime);
        } else if (latestPickup.scheduledDateTime) {
          leftbound = new Date(latestPickup.scheduledDateTime);
        } else {
          leftbound = epoch;
        }
      } else {
        leftbound = epoch;
      }
    }

    if (stop.lateDateTime) {
      rightbound = new Date(stop.lateDateTime);
    } else if (stop.scheduledDateTime) {
      rightbound = new Date(stop.scheduledDateTime);
    }
  }

  return [leftbound, rightbound];
};

/**
 * True when the stop's planning window ends before it starts. CMS's Order Entry never saves such a stop, but
 * orderload.svc's last-stop override (PROF-8661) does, and GraphHopper rejects the whole request.
 */
export const hasInvertedTimeWindow = (stop: NestedJobStop, job: Job): boolean => {
  const [leftbound, rightbound] = getStopTimeWindow(stop, job);
  return !!leftbound && !!rightbound && leftbound.getTime() > rightbound.getTime();
};

/** Build a unique GH vehicle_id from a driver-like object. */
export const toVehicleId = (driver: {driverId?: number | null; manifestDriverId?: number | null}): string =>
  driver.manifestDriverId ? `manifest:${driver.manifestDriverId}` : `driver:${driver.driverId}`;

/**
 * Resolves a GH vehicle_id ("driver:{driverId}" | "manifest:{manifestDriverId}")
 * to a driver from a list. Works with any driver-like type that has driverId
 * and manifestDriverId fields.
 */
export const findDriverByVehicleId = <T extends {driverId?: number | null; manifestDriverId?: number | null}>(
  vehicleId: string | null | undefined,
  drivers: T[]
): T | undefined => {
  if (!vehicleId) return undefined;
  const [prefix, idStr] = vehicleId.split(":");
  if (prefix === "manifest" && idStr) {
    return drivers.find((d) => d.manifestDriverId === Number(idStr));
  }
  if (prefix === "driver" && idStr) {
    return drivers.find((d) => !d.manifestDriverId && d.driverId === Number(idStr));
  }
  return undefined;
};

/**
 * DP-3219: the instant a route is measured from — the submitted driver start, unless it is
 * already in the past, in which case now. The same rule the route-planning lambda applies
 * (clampStartDateTimeToNow in nxtgen-services) to every routeEta it returns, so a route the
 * frontend prices itself — a frozen manifest, the Added-cost baseline — starts from the same
 * instant as the routes GraphHopper priced. Without it a plan run at 10:05 for a driver
 * starting at 10:00 carried five phantom minutes on every frontend-priced route.
 */
export const clampStartDateTimeToNow = (startTime: string | null | undefined, now: Date = new Date()): Date => {
  if (!startTime) return now;
  const submitted = new Date(startTime);
  if (isNaN(submitted.getTime())) return now;
  return submitted >= now ? submitted : now;
};

export const generateGraphhopperAssignmentDetails = (
  solution: RoutePlanningSolution,
  startDate: string,
  dismissJobIds: number[],
  submittedInput: SubmitRoutePlanningInput,
  isEtaFromOptimizationEnabled: boolean,
  routeEtas: RoutePlanningRouteEtas[] | null = null,
  /** DP-3219: Manifest driver IDs with existing stop failures — rebuild from original stops. */
  affectedManifestDriverIds: number[] = [],
  /** DP-3219: Original manifest stops — required when affectedManifestDriverIds is non-empty. */
  manifestStopsMap: Record<number, ManifestStop[]> = {}
): AssignmentDetailWithEndDateTime[] => {
  const assignmentDetails: AssignmentDetailWithEndDateTime[] = [];
  const submittedDrivers = (submittedInput?.drivers ?? []).filter((d): d is NonNullable<typeof d> => d != null);
  const routeEtasByVehicleId = new Map((routeEtas ?? []).map((routeEta) => [routeEta.vehicleId, routeEta]));
  // DP-3219: freezing a manifest is only safe once the set is closed over the
  // stops GH moved across manifest boundaries — see
  // closeAffectedManifestsOverMovedStops. Idempotent, so callers that already
  // closed the set (analyzeManifestFailures) pay nothing.
  const affectedMdIdSet = closeAffectedManifestsOverMovedStops(
    solution.routes ?? [],
    new Set(affectedManifestDriverIds),
    manifestStopsMap
  );
  const originalMdIdByJobStopId = buildOriginalManifestIndexByJobStopId(manifestStopsMap);
  const restoredMdIds = new Set<number>();
  solution.routes?.forEach((route) => {
    const driver = findDriverByVehicleId(route.vehicle_id, submittedDrivers);
    if (!driver || !route.activities) {
      log.warn("Driver not found for route", route);
      return;
    }

    const stopActivities = getStopActivities(route.activities);
    const manifestDriverId = driver.manifestDriverId;

    // DP-3219: For affected manifests, rebuild sequences from original stops.
    // GH geometry is stripped in handleResult; RoutesResultMap geocodes missing
    // stop addresses and fetches fresh polylines/ETAs via getRouteWithEtas.
    if (manifestDriverId && affectedMdIdSet.has(manifestDriverId)) {
      restoredMdIds.add(manifestDriverId);
      assignmentDetails.push(
        buildFrozenManifestDetail(driver, manifestDriverId, manifestStopsMap, startDate, submittedInput)
      );
      return;
    }

    // DP-3175: Existing manifests always appear on the map, even if empty
    if (stopActivities.length === 0 && !manifestDriverId) {
      return;
    }

    const jobIds = new Set<number>();
    // DP-3228: a Set, not an array. getUniqueActivities de-duplicates on
    // `location_id`+`type`, so a stop that is BOTH the pickup and the delivery of a
    // shipment survives twice — which happens for every pickup-only / delivery-only
    // job (self-paired shipments) and for every Both stop (delivery of one shipment,
    // pickup of the next). Pushing the jobStopId twice then breaks AG Grid's getRowId,
    // double-counts stops in Route Cost, shifts manifestSequence, and makes the
    // route-signature baseline in RoutesResultMap never match — the DP-3100 bug.
    // This was a Set until edc219a95 (DP-3175) changed it, correctly at the time:
    // under the Service model one stop yields one activity and duplicates cannot
    // occur, so restoring the Set is a no-op there and a fix on the shipment path.
    // Insertion order is preserved, which is what the sequence semantics rely on.
    const sequences = new Set<number>();
    let isRouteChanged = false;

    // Add stops from route activities (backend provides all stops including
    // completed ones for untouched/rebuilt manifest routes)
    stopActivities.forEach((activity) => {
      const [jobId, jobStopId] = extractJobAndJobStopIdsFromActivity(activity);
      if (jobId && dismissJobIds?.includes(jobId)) {
        isRouteChanged = true;
        return;
      }
      // DP-3219: the stop's own manifest is frozen, so the branch above already
      // handed it back. Keeping it here as well would put one jobStopId on two
      // routes, and the backend would move it while the manifest still holds it.
      const originalMdId = jobStopId != null ? originalMdIdByJobStopId.get(jobStopId) : undefined;
      if (originalMdId != null && affectedMdIdSet.has(originalMdId)) {
        isRouteChanged = true;
        return;
      }
      if (jobId && jobStopId) {
        jobIds.add(jobId);
        sequences.add(jobStopId);
      }
    });

    if (jobIds.size === 0 && !manifestDriverId) {
      return;
    }

    // Server-computed optimization ETAs (DP-3121): the route-planning lambda
    // owns the dwell walk, the wait-for-early-time-window clamp, and the
    // start-time fallback; the frontend only consumes the returned values.
    // Routes with dismissed jobs no longer match the solved sequence, so their
    // server ETAs are discarded (requestEtaIfEmpty covers them at create time).
    const routeEta = !isRouteChanged ? routeEtasByVehicleId.get(route.vehicle_id ?? "") : undefined;

    // The server start is deliberately NOT gated on isEtaFromOptimizationEnabled:
    // the start-time derivation (submitted start, else scheduled minus driving
    // time, never in the past — clamping now happens server-side at poll time)
    // was never flag-gated before it moved to the lambda, and the server value
    // is the only remaining source of the scheduled-time fallback. Clamping it
    // again here would desync the start from the ETAs computed off it.
    const driverStartDateTime: Date = routeEta?.startDateTime
      ? new Date(routeEta.startDateTime)
      : clampStartDateTimeToNow(driver.startTime);

    const useServerEtas = isEtaFromOptimizationEnabled && !isRouteChanged && routeEta != null;
    const fallbackGeneratedDateTime = new Date().toISOString();
    // DP-3228: de-duplicate by jobStopId for the same reason `sequences` is a Set —
    // computeRouteEtas emits one ETA per ACTIVITY, and under the shipment model a
    // self-paired or Both stop is two activities at one location. Keep the first: it
    // is the earlier leg in route order, i.e. the arrival. Without this,
    // optimizationEtas and sequences disagree in length, and RoutePlanningView
    // submits optimizationEtas verbatim to processRoutePlanning whenever the plan is
    // unmodified — so the duplicate would be written, not just displayed. (It is
    // invisible in manual QA of a modified plan, because rebuildAssignmentDetails
    // re-derives the ETAs from assignedStops.)
    const seenEtaStopIds = new Set<number>();
    const stopEtas: EtaItemInput[] = useServerEtas
      ? (routeEta.etas ?? [])
          .filter(
            (item): item is {jobStopId: number; eta: string; etaSource?: string; generatedDateTime?: string} =>
              item?.jobStopId != null && !!item.eta
          )
          .filter((item) => {
            if (seenEtaStopIds.has(item.jobStopId)) return false;
            seenEtaStopIds.add(item.jobStopId);
            return true;
          })
          .map((item) => ({
            jobStopId: item.jobStopId,
            eta: item.eta,
            etaSource: item.etaSource ?? ETASourceType.ROUTE_PLANNING,
            generatedDateTime: item.generatedDateTime ?? fallbackGeneratedDateTime
          }))
      : [];
    // DP-3175 B04: endDateTime is always read from the server when available,
    // regardless of the etaFromOptimization flag. The flag gates per-stop ETAs
    // (shown in the detail grid), but the route-level endDateTime is needed for
    // the master grid's End Time column and Route Cost duration in all cases.
    // routeEta is already undefined for changed routes, so no extra guard needed.
    const endTime = routeEta?.endDateTime ?? undefined;

    assignmentDetails.push({
      submittedDriver: {...driver, startTime: driverStartDateTime.toISOString()},
      dwellTimeSeconds: submittedInput.dwellTimeSeconds ?? 0,
      jobIds: Array.from(jobIds),
      sequences: Array.from(sequences),
      startDate: startDate,
      optimizationEtas: stopEtas,
      endDateTime: endTime,
      // DP-3175 B05: always request backend ETA computation when the frontend
      // didn't supply per-stop ETAs. The etaFromOptimization flag gates whether
      // the frontend USES server ETAs in the UI, not whether the backend should
      // compute them at manifest-creation time.
      requestEtaIfEmpty: true
    });
  });

  // DP-3219: a frozen manifest GH emitted no route for — every one of its stops
  // moved elsewhere — was never visited above, yet the loop has just stripped
  // those stops from the routes that took them. Restoring it here is what keeps
  // them assigned; without it the closure would lose the very stops it protects.
  for (const mdId of affectedMdIdSet) {
    if (restoredMdIds.has(mdId) || (manifestStopsMap[mdId] ?? []).length === 0) continue;
    const driver = submittedDrivers.find((d) => d.manifestDriverId === mdId);
    if (!driver) {
      log.warn("No submitted driver for frozen manifest", mdId);
      continue;
    }
    assignmentDetails.push(buildFrozenManifestDetail(driver, mdId, manifestStopsMap, startDate, submittedInput));
  }

  return assignmentDetails;
};

/**
 * DP-3219: the assignment detail that hands a frozen manifest back its original
 * stops. GH geometry is stripped in handleResult; RoutesResultMap geocodes the
 * missing stop addresses and fetches fresh polylines/ETAs via getRouteWithEtas.
 */
const buildFrozenManifestDetail = (
  driver: RoutePlanningDriverInput,
  manifestDriverId: number,
  manifestStopsMap: Record<number, ManifestStop[]>,
  startDate: string,
  submittedInput: SubmitRoutePlanningInput
): AssignmentDetailWithEndDateTime => ({
  // DP-3219: this route is priced by the frontend (the map's getRouteWithEtas reads
  // row.startDateTime, which is this startTime), so it has to start from the same
  // instant the lambda gives every GraphHopper route — never in the past.
  submittedDriver: {...driver, startTime: clampStartDateTimeToNow(driver.startTime).toISOString()},
  dwellTimeSeconds: submittedInput.dwellTimeSeconds ?? 0,
  // DP-3219: read off the same allowlist as `sequences`, so the detail never claims a job
  // it hands back no stop for.
  jobIds: [
    ...new Set((manifestStopsMap[manifestDriverId] ?? []).filter(isSupportedStopStatus).map((s) => s.job.jobId))
  ],
  sequences: getOriginalManifestSequence(manifestStopsMap, manifestDriverId),
  startDate,
  optimizationEtas: [],
  requestEtaIfEmpty: true
});

const isStopActivity = (activity: RoutePlanningActivity): boolean => {
  return activity.type !== "start" && activity.type !== "end";
};

const getUniqueActivities = (activities: RoutePlanningActivity[]): RoutePlanningActivity[] => {
  const uniqueMap = new Map<string, RoutePlanningActivity>();
  activities.forEach((act) => {
    const key = `${act.location_id}_${act.type}`;
    if (!uniqueMap.has(key)) {
      uniqueMap.set(key, act);
    }
  });
  return Array.from(uniqueMap.values());
};

export const getStopActivities = (activities: RoutePlanningActivity[] | null | undefined): RoutePlanningActivity[] => {
  if (!activities) return [];
  return getUniqueActivities(activities.filter(isStopActivity));
};

/**
 * The ids the backend gives GraphHopper and we read back (DP-3237):
 *   shipment id:      "{prefix}:{jobId}:{pickupStopId}:{deliveryStopId}"
 *   service id:       "{prefix}:{jobId}:{jobStopId}"
 *   leg location_id:  "{prefix}:{jobId}:{jobStopId}"
 *   prefix: "newjob" (picked on the Jobs grid) or "manifestjob" (on an existing manifest)
 * Anchored patterns, integers only; anything else is null, including the pre-DP-3237
 * forms. Vehicle ids ("driver:…", "manifest:…") are a separate namespace.
 * Twin: route-planning/utils/graphhopper-ids-utils.ts, same contract-test table.
 */
export type GhJobKind = "manifest" | "job";
export interface GhJobStopsId {
  kind: GhJobKind;
  jobId: number;
  /** One id for a service, [pickup, delivery] for a shipment (equal when self-paired). */
  jobStopIds: number[];
}
export interface GhLocationId {
  kind: GhJobKind;
  jobId: number;
  jobStopId: number;
}

interface GhIdPattern {
  pattern: RegExp;
  kind: GhJobKind;
}

// Capture groups: jobId, then the stop ids.
const GH_JOB_STOPS_ID_PATTERNS: readonly GhIdPattern[] = [
  {pattern: /^manifestjob:(\d+):(\d+)$/, kind: "manifest"},
  {pattern: /^manifestjob:(\d+):(\d+):(\d+)$/, kind: "manifest"},
  {pattern: /^newjob:(\d+):(\d+)$/, kind: "job"},
  {pattern: /^newjob:(\d+):(\d+):(\d+)$/, kind: "job"}
];

const GH_LOCATION_ID_PATTERNS: readonly GhIdPattern[] = [
  {pattern: /^manifestjob:(\d+):(\d+)$/, kind: "manifest"},
  {pattern: /^newjob:(\d+):(\d+)$/, kind: "job"}
];

const matchGhId = (
  id: string | null | undefined,
  patterns: readonly GhIdPattern[]
): {kind: GhJobKind; ids: number[]} | null => {
  if (!id) return null;
  for (const {pattern, kind} of patterns) {
    const match = id.match(pattern);
    if (match) return {kind, ids: match.slice(1).map(Number)};
  }
  return null;
};

/** A service / shipment id (as echoed in `unassigned.details`), or null. */
export const parseGhJobStopsId = (id: string | null | undefined): GhJobStopsId | null => {
  const match = matchGhId(id, GH_JOB_STOPS_ID_PATTERNS);
  if (!match) return null;
  const [jobId, ...jobStopIds] = match.ids;
  return {kind: match.kind, jobId, jobStopIds};
};

/** A route activity's location_id, or null. */
export const parseGhLocationId = (id: string | null | undefined): GhLocationId | null => {
  const match = matchGhId(id, GH_LOCATION_ID_PATTERNS);
  if (!match) return null;
  const [jobId, jobStopId] = match.ids;
  return {kind: match.kind, jobId, jobStopId};
};

export const extractJobAndJobStopIdsFromActivity = (
  activity: RoutePlanningActivity | null
): [number | null, number | null] => {
  const parsed = parseGhLocationId(activity?.location_id);
  return parsed ? [parsed.jobId, parsed.jobStopId] : [null, null];
};

/**
 * location_ids of route stops the grammar does not recognise. Such a stop would be
 * silently left out of the created route, so the caller fails closed (DP-3237).
 * Activities without a location_id (breaks) are not stops and are skipped.
 */
export const getUnrecognisedRouteStopIds = (
  solution: {routes?: Array<{activities?: RoutePlanningActivity[] | null}> | null} | null | undefined
): string[] =>
  (solution?.routes ?? []).flatMap((route) =>
    getStopActivities(route.activities)
      .filter((activity) => activity.location_id && !parseGhLocationId(activity.location_id))
      .map((activity) => activity.location_id!)
  );

/**
 * Extracts unique jobIds from unassigned details that belong to manifest stops.
 */
export const getUnassignedManifestJobIds = (
  unassignedDetails: Array<{id?: string | null; code?: number | null; reason?: string | null}>
): number[] => {
  const jobIds = new Set<number>();
  for (const detail of unassignedDetails) {
    const parsed = parseGhJobStopsId(detail.id);
    if (parsed?.kind === "manifest") {
      jobIds.add(parsed.jobId);
    }
  }
  return Array.from(jobIds);
};

/**
 * Returns the original manifest stop sequence (jobStopIds) sorted by manifestSequence.
 * Shared by handleCreateRoutes, DriverResultList untouched memo, and handleResult rebuild.
 *
 * DP-3219: only stops in a status route planning is defined over (A/Q/C) belong in the
 * sequence. This is the one derivation of "what this manifest held before the solve", so
 * it is the place the frontend's notion of a manifest route is made to agree with the
 * backend's — the lambda sends GraphHopper A/Q and injects C, and nothing else can ever
 * come back on the route. Every caller inherits that: the untouched comparison below, the
 * frozen-manifest rebuild, and DriverResultList's untouched memo.
 */
export const getOriginalManifestSequence = (
  manifestStopsMap: Record<number, ManifestStop[]>,
  manifestDriverId: number
): number[] =>
  (manifestStopsMap[manifestDriverId] ?? [])
    .filter(isSupportedStopStatus)
    .sort((a, b) => a.manifestSequence - b.manifestSequence)
    .map((s) => s.jobStopId);

/**
 * Checks whether a sequence of jobStopIds matches the original manifest stops.
 * A manifest is untouched when its stops match the original sequence exactly
 * and no new stops were added.
 */
export const isManifestRouteUntouched = (
  manifestStopsMap: Record<number, ManifestStop[]>,
  manifestDriverId: number,
  currentSequence: number[]
): boolean => {
  // DP-3219: both sides of the comparison come from the same derivation, so the set of
  // stops and their order are read off one list. Building the set straight from
  // manifestStopsMap instead let a stop the solver never saw sit on only one side, and a
  // manifest that nobody had touched was submitted — and the backend then unassigned that
  // stop, because it is in the mapping but on no route.
  const originalSequence = getOriginalManifestSequence(manifestStopsMap, manifestDriverId);
  const originalStopIds = new Set(originalSequence);
  if (currentSequence.some((id) => !originalStopIds.has(id))) return false;
  return (
    currentSequence.length === originalSequence.length && currentSequence.every((id, i) => id === originalSequence[i])
  );
};

/** jobStopId → {lat, lon} for every manifest stop — GraphHopper's answer where it has one, geocoded otherwise. */
export type ManifestStopCoords = Record<number, {lat: number; lon: number}>;

type GeocodeFn = (address: string) => Promise<{latitude: number; longitude: number} | undefined>;

/** The address key a manifest stop is geocoded under. GraphHopper's `address.name` is the same join. */
const toManifestStopAddress = (stop: ManifestStop): string =>
  [stop.address, stop.city, stop.state, stop.zip].filter(Boolean).join(", ").trim();

/**
 * DP-3219: one lookup per address, even when two callers want it at the same moment.
 * getAddressPositionWithCache caches the ANSWER, not the request in flight, so the manifests'
 * start/end locations and dropped stops geocode concurrently, and a depot shared by eight
 * drivers would be eight billable lookups where a serial loop paid for one.
 */
const dedupeGeocode = (geocodeFn: GeocodeFn): GeocodeFn => {
  const inFlight = new Map<string, ReturnType<GeocodeFn>>();
  return (address) => {
    const pending = inFlight.get(address);
    if (pending) return pending;
    const request = geocodeFn(address);
    inFlight.set(address, request);
    return request;
  };
};

/**
 * DP-3219: the on-duty seconds a fetched route represents.
 *
 * parseRouteWithEtasResult returns miles and an end time but no duration, so it is derived the
 * way useRoutePlanningRows derives a row's `duration` — route end minus the driver start that
 * was sent with the request. Deriving it any other way would leave the "before" figure
 * incomparable with the "after" the grid shows, which is the only reason it exists.
 *
 * Returns null when the lambda gave back no end time: a route contributing miles but no hours
 * would price the manifest's whole pre-existing on-duty time as ADDED, and reading zero for it
 * is by far the safer miss.
 */
const toRouteTransitSeconds = (startDateTime: string, endDateTime: string | undefined): number | null => {
  if (!endDateTime) return null;
  const elapsedMs = new Date(endDateTime).getTime() - new Date(startDateTime).getTime();
  if (!Number.isFinite(elapsedMs)) return null;
  return Math.max(0, elapsedMs) / 1000;
};

/**
 * DP-3219: Resolve a coordinate for every manifest stop before the map renders.
 *
 * 1. Seed GraphHopper's coordinates into the central geocode cache. The backend geocoded that
 *    address, so it overwrites whatever the frontend resolved earlier — and every later lookup
 *    of the address (the map's markers, computeBeforeTotals) reads the same point, so two stops
 *    at one address cluster instead of sitting a few metres apart.
 * 2. Geocode the addresses GraphHopper never placed: the stops it dropped off an affected
 *    manifest. Only A/Q stops — a completed one is behind the driver, and a stop outside the
 *    allowlist is not part of the plan at all, so geocoding it would put it on the map.
 * 3. Read every stop's coordinate back out of what steps 1 and 2 resolved. A non-affected stop
 *    whose address GraphHopper did not name is left out rather than geocoded: the map falls
 *    back to the activity's own coordinate for it, and a lookup here would be a billable call
 *    for a point the backend already has.
 *
 * Routes are deliberately NOT fetched here. The map's polyline effect fetches whatever has no
 * geometry and is the one writer of miles/ETAs into the rows; a second writer here is what the
 * prefetchedRouteCache design (removed in this change) had to keep in step with it.
 */
export const geocodeManifestStops = async (
  result: GetRoutePlanningResultResponse,
  affectedManifestDriverIds: number[],
  manifestStopsMap: Record<number, ManifestStop[]>,
  geocodeFn: GeocodeFn,
  seedGeocodeFn: (address: string, position: {latitude: number; longitude: number}) => void
): Promise<ManifestStopCoords> => {
  const stopAddressMap = new Map<number, string>();
  const coordByAddress = new Map<string, {lat: number; lon: number}>();
  const affectedIds = new Set(affectedManifestDriverIds);

  // 1. GraphHopper's coordinates, keyed by the address it geocoded them for.
  for (const route of result.solution?.routes ?? []) {
    for (const activity of getStopActivities(route.activities)) {
      const [, jobStopId] = extractJobAndJobStopIdsFromActivity(activity);
      const lat = activity.address?.lat;
      const lon = activity.address?.lon;
      if (!jobStopId || lat == null || lon == null) continue;
      const addressKey = activity.address?.name?.trim() ?? "";
      if (!addressKey) continue;
      seedGeocodeFn(addressKey, {latitude: lat, longitude: lon});
      coordByAddress.set(addressKey, {lat, lon});
      stopAddressMap.set(jobStopId, addressKey);
    }
  }

  // 2. The manifests' own stops, keyed by the address the frontend joins for them.
  for (const stops of Object.values(manifestStopsMap)) {
    for (const stop of stops) {
      if (!isActiveOrQueued(stop)) continue;
      const address = toManifestStopAddress(stop);
      if (address) stopAddressMap.set(stop.jobStopId, address);
    }
  }

  // 3. The stops GraphHopper dropped off an affected manifest. Issued together, not one at a
  //    time — a serial chain put every dropped stop's geocode end to end in front of the map.
  const addressesToGeocode = new Set<string>();
  for (const [mdIdStr, stops] of Object.entries(manifestStopsMap)) {
    if (!affectedIds.has(Number(mdIdStr))) continue;
    for (const stop of stops) {
      if (!isActiveOrQueued(stop)) continue;
      const address = stopAddressMap.get(stop.jobStopId);
      if (address && !coordByAddress.has(address)) addressesToGeocode.add(address);
    }
  }
  await Promise.all(
    [...addressesToGeocode].map(async (address) => {
      try {
        const pos = await geocodeFn(address);
        if (pos) coordByAddress.set(address, {lat: pos.latitude, lon: pos.longitude});
      } catch (err) {
        log.warn("[geocodeManifestStops] geocode failed for", address, err);
      }
    })
  );

  const stopCoords: ManifestStopCoords = {};
  for (const [jobStopId, address] of stopAddressMap) {
    const coord = coordByAddress.get(address);
    if (coord) stopCoords[jobStopId] = coord;
  }
  return stopCoords;
};

/**
 * DP-3219: the "before" half of Added cost — what the selected manifests were already costing
 * before the solve. Measured once, here, from the manifests' own stops; PlanningResult reads
 * the stored figure and never recomputes it, so the dispatcher's edits on the map move only
 * the "after" side.
 *
 * The rules, confirmed by Vuong Vu on 2026-09-26:
 * - New drivers contribute nothing — they had no work before the plan.
 * - A manifest counts as a driver already on the road when it holds ANY assigned stop,
 *   completed or not: the driver is out either way.
 * - Pickup/delivery counts and the route (miles, hours) cover only the stops still to be
 *   driven (A/Q). A completed stop's cost is sunk, and summarizeRouteTotals skips it on the
 *   after side too, so the subtraction stays symmetric.
 * - Miles and hours come from getRouteWithEtas over the manifest's original stops in original
 *   order — every manifest, untouched ones included. The rows cannot supply them: a row carries
 *   GraphHopper's SOLVED route, which already holds the job it inserted, so reading "before"
 *   off it priced an inserted job at zero miles.
 *
 * A manifest whose route the lambda cannot price still contributes its stops and its driver,
 * but no miles or hours — the safer miss, since a missing baseline reads as ADDED cost rather
 * than as a saving.
 *
 * Returns null when no selected manifest holds a stop: there is no before state, and the cost
 * dialog then hides the Added-cost toggle.
 */
export const computeBeforeTotals = async (
  manifestStopsMap: Record<number, ManifestStop[]>,
  selectedDrivers: Array<{
    manifestDriverId?: number | null;
    startLocation?: string | null;
    endLocation?: string | null;
    startDate?: any;
    startTime?: any;
    startTimeZone?: string | null;
  }>,
  dwellSeconds: number,
  stopCoords: ManifestStopCoords,
  geocodeFn: GeocodeFn,
  getRouteWithEtasFn: (variables: any) => Promise<any>
): Promise<RouteCostTotals | null> => {
  const nonBlankMdIds = Object.entries(manifestStopsMap)
    .filter(([id, stops]) => Number(id) > 0 && stops.length > 0)
    .map(([id]) => Number(id));
  if (nonBlankMdIds.length === 0) return null;

  const geocodeOnce = dedupeGeocode(geocodeFn);
  const activeStopsOf = (mdId: number): ManifestStop[] =>
    (manifestStopsMap[mdId] ?? []).filter(isActiveOrQueued).sort((a, b) => a.manifestSequence - b.manifestSequence);

  let totalDeliveryStops = 0;
  let totalPickupStops = 0;
  for (const mdId of nonBlankMdIds) {
    for (const stop of activeStopsOf(mdId)) {
      if (isPickupStop(stop)) totalPickupStops += 1;
      else if (isDeliveryOrBothStop(stop)) totalDeliveryStops += 1;
    }
  }

  /** The manifest's own route, over its own stops in its own order, priced by the ETA lambda. */
  const routeManifest = async (mdId: number): Promise<{miles: number; transitSeconds: number} | null> => {
    const stops = activeStopsOf(mdId);
    if (stops.length === 0) return null;
    const driver = selectedDrivers.find((d) => d.manifestDriverId === mdId);
    if (!driver) return null;

    let startPos: {latitude: number; longitude: number} | undefined;
    let endPos: {latitude: number; longitude: number} | undefined;
    try {
      [startPos, endPos] = await Promise.all([
        driver.startLocation ? geocodeOnce(driver.startLocation) : undefined,
        driver.endLocation ? geocodeOnce(driver.endLocation) : undefined
      ]);
    } catch (err) {
      log.warn("[computeBeforeTotals] geocode failed for manifest driver location", mdId, err);
      return null;
    }

    const routePoints: Array<{
      id: string;
      coordinates: number[];
      earlyDateTime?: string;
      scheduledDateTime?: string;
      stopType?: string;
    }> = [];
    if (startPos) routePoints.push({id: "start", coordinates: [startPos.longitude, startPos.latitude]});
    for (const stop of stops) {
      const coord = stopCoords[stop.jobStopId];
      if (!coord) continue;
      routePoints.push({
        id: String(stop.jobStopId),
        coordinates: [coord.lon, coord.lat],
        earlyDateTime: stop.earlyDateTime ?? undefined,
        scheduledDateTime: stop.scheduledDateTime ?? undefined,
        stopType: stop.stopType ?? undefined
      });
    }
    if (endPos) routePoints.push({id: "end", coordinates: [endPos.longitude, endPos.latitude]});
    if (routePoints.length < 2) return null;

    // The instant handleSubmit sends as the driver's startTime, so this route is measured from
    // the same start the rows are. `startTime` on its own is the TimePicker's value, and its
    // DATE is the day it was picked: routing a plan for a later day from today makes the ETA
    // lambda wait until the stops' windows open, the fetched route spans days, and the "before"
    // hours swallow the whole plan — Added transit time then reads 0h 0m. Clamped to now like
    // every other route start, or a start already behind the clock would inflate the "before"
    // hours and understate the added time.
    const startDateTime = clampStartDateTimeToNow(
      combineDatesAndTime(driver.startDate, driver.startTime, driver.startTimeZone).toISOString()
    ).toISOString();

    try {
      const response = await getRouteWithEtasFn({
        variables: {routePoints, startDateTime, dwellTimeSeconds: dwellSeconds, service: "route-planning"}
      });
      const parsed = parseRouteWithEtasResult(response.data?.getRouteWithEtas?.items?.[0], dwellSeconds);
      if (!parsed) return null;
      const transitSeconds = toRouteTransitSeconds(startDateTime, parsed.endDateTime);
      if (transitSeconds == null) return null;
      // Whole minutes per manifest, exactly as summarizeRouteTotals truncates each row on the
      // after side — the subtraction has to lose the same seconds on both sides.
      return {miles: parsed.miles, transitSeconds: Math.floor(transitSeconds / 60) * 60};
    } catch (err) {
      log.warn("[computeBeforeTotals] getRouteWithEtas failed for manifest", mdId, err);
      return null;
    }
  };

  // allSettled, not all: each manifest already swallows its own getRouteWithEtas failure, and
  // this keeps anything else that throws (a geocode, say) from discarding every other
  // manifest's baseline the way a rejected Promise.all would.
  const routed = await Promise.allSettled(nonBlankMdIds.map(routeManifest));
  let totalMiles = 0;
  let totalTransitSeconds = 0;
  for (const outcome of routed) {
    if (outcome.status !== "fulfilled" || !outcome.value) continue;
    totalMiles += outcome.value.miles;
    totalTransitSeconds += outcome.value.transitSeconds;
  }

  return {
    totalMiles: Math.round(totalMiles * 100) / 100,
    totalTransitSeconds: Math.round(totalTransitSeconds),
    totalRoutes: nonBlankMdIds.length,
    totalDeliveryStops,
    totalPickupStops
  };
};

/**
 * DP-3219: Clear GraphHopper's route data for the manifest routes the map must not draw as-is.
 *
 * - An emptied manifest (every stop moved elsewhere, `allowMovingExistingWork` on) drives
 *   nowhere: no polyline, 0 miles, and no end time or duration in the grid — only the driver pin.
 * - A frozen (affected) manifest was handed back its ORIGINAL stops, so GraphHopper's partial
 *   route and distance for it are wrong. With the geometry gone the map's polyline effect takes
 *   the "no polyline" path and fetches the manifest's real route via getRouteWithEtas, and the
 *   restore effect in useRoutePlanningRows knows not to write GraphHopper's figures back over it.
 *
 * Mutates the emptied manifests' assignment details (endDateTime, optimizationEtas) in place —
 * they were read off GraphHopper's route, which for an emptied manifest is a start→end drive
 * that no longer exists. The result itself is copied, never mutated.
 */
export const stripManifestRouteData = (
  result: GetRoutePlanningResultResponse,
  assignmentDetails: AssignmentDetailWithEndDateTime[],
  affectedManifestDriverIds: number[] = []
): GetRoutePlanningResultResponse => {
  const strippedMdIds = new Set<number>(affectedManifestDriverIds);
  for (const detail of assignmentDetails) {
    const mdId = detail.submittedDriver.manifestDriverId;
    if (mdId && detail.sequences.length === 0) {
      strippedMdIds.add(mdId);
      detail.endDateTime = undefined;
      detail.optimizationEtas = [];
    }
  }
  if (strippedMdIds.size === 0) return result;

  const strippedVehicleIds = new Set([...strippedMdIds].map((mdId) => `manifest:${mdId}`));
  const routes = (result.solution?.routes ?? []).map((route) =>
    route.vehicle_id && strippedVehicleIds.has(route.vehicle_id)
      ? {...route, points: [], distance: 0, transport_time: 0}
      : route
  );
  const routeEtas = (result.routeEtas ?? []).filter((eta) => !strippedVehicleIds.has(eta.vehicleId));
  return {...result, solution: {...result.solution, routes}, routeEtas};
};

/**
 * DP-3219: Given unassigned details containing manifest stops, find which
 * manifest driver IDs are affected (i.e., have at least one unassigned stop).
 *
 * Keyed on the jobStopId segments of a manifest id, not the jobId.
 * One job can legitimately sit on two manifests at once: the backend sends only its
 * active stops, so a job whose pickup has been cleared leaves that stop on the old
 * manifest while ManifestDetailsV2's reassign flow moves the rest to another driver.
 * Matching on jobId froze both — and freezing a manifest that lost nothing needlessly
 * dismisses every new job GraphHopper had placed on its route.
 * A manifest shipment id names two stops that can sit on different manifests, so
 * every stop id counts (DP-3237).
 */
export const getAffectedManifestDriverIds = (
  unassignedDetails: Array<{id?: string | null}>,
  manifestStopsMap: Record<number, ManifestStop[]>
): Set<number> => {
  // Collect the stops GraphHopper could not place
  const unassignedJobStopIds = new Set<number>();
  for (const detail of unassignedDetails) {
    const parsed = parseGhJobStopsId(detail.id);
    if (parsed?.kind === "manifest") {
      parsed.jobStopIds.forEach((jobStopId) => unassignedJobStopIds.add(jobStopId));
    }
  }

  // Find which manifest driver IDs own those stops
  const affectedMdIds = new Set<number>();
  for (const [mdIdStr, stops] of Object.entries(manifestStopsMap)) {
    if (stops.some((s) => unassignedJobStopIds.has(s.jobStopId))) {
      affectedMdIds.add(Number(mdIdStr));
    }
  }
  return affectedMdIds;
};

/** The manifest a GH route belongs to, or null for a new-driver route. */
const getRouteManifestDriverId = (vehicleId: string | null | undefined): number | null => {
  if (!vehicleId?.startsWith("manifest:")) return null;
  const manifestDriverId = Number(vehicleId.slice("manifest:".length));
  return Number.isFinite(manifestDriverId) ? manifestDriverId : null;
};

/** jobStopId → the manifest that owned the stop before the solve. */
const buildOriginalManifestIndexByJobStopId = (
  manifestStopsMap: Record<number, ManifestStop[]>
): Map<number, number> => {
  const originalMdIdByJobStopId = new Map<number, number>();
  for (const [mdIdStr, stops] of Object.entries(manifestStopsMap)) {
    for (const stop of stops) {
      originalMdIdByJobStopId.set(stop.jobStopId, Number(mdIdStr));
    }
  }
  return originalMdIdByJobStopId;
};

/**
 * DP-3219: Grow the set of frozen manifests until it is closed under the stop
 * moves GH made across manifest boundaries.
 *
 * A frozen manifest is restored to its original stops, so it drops whatever GH
 * moved onto it from manifest N — and N's own route no longer lists that stop
 * either. The stop is then on no route and the backend unassigns it, which Jake
 * Bielecki ruled out on 2026-09-16: "Assigned jobs will never be unassigned."
 * Freezing N as well puts the stop back where it started.
 *
 * Only the donor propagates: a manifest that merely *received* a stop is not
 * frozen by that alone, so manifests GH shuffled among themselves keep their new
 * plan — which is what the dispatcher asked for. The `allowMovingExistingWork`
 * toggle is deliberately not consulted: the invariant holds either way, and with
 * the toggle off the backend pins each stop to its own vehicle, no boundary is
 * crossed, and the closure adds nothing.
 */
export const closeAffectedManifestsOverMovedStops = (
  routes: Array<{vehicle_id?: string | null; activities?: RoutePlanningActivity[] | null}>,
  initialAffectedMdIds: Set<number>,
  manifestStopsMap: Record<number, ManifestStop[]>
): Set<number> => {
  const affectedMdIds = new Set(initialAffectedMdIds);
  if (affectedMdIds.size === 0) return affectedMdIds;

  const originalMdIdByJobStopId = buildOriginalManifestIndexByJobStopId(manifestStopsMap);
  const donorMdIdsByHostMdId = new Map<number, Set<number>>();
  for (const route of routes) {
    const hostMdId = getRouteManifestDriverId(route.vehicle_id);
    if (hostMdId == null) continue;
    for (const activity of getStopActivities(route.activities)) {
      const [, jobStopId] = extractJobAndJobStopIdsFromActivity(activity);
      if (jobStopId == null) continue;
      const donorMdId = originalMdIdByJobStopId.get(jobStopId);
      if (donorMdId == null || donorMdId === hostMdId) continue;
      const donors = donorMdIdsByHostMdId.get(hostMdId) ?? new Set<number>();
      donors.add(donorMdId);
      donorMdIdsByHostMdId.set(hostMdId, donors);
    }
  }

  const pending = [...affectedMdIds];
  while (pending.length > 0) {
    const hostMdId = pending.pop()!;
    for (const donorMdId of donorMdIdsByHostMdId.get(hostMdId) ?? []) {
      if (affectedMdIds.has(donorMdId)) continue;
      affectedMdIds.add(donorMdId);
      pending.push(donorMdId);
    }
  }
  return affectedMdIds;
};

/**
 * DP-3219: Find new (unassigned) job IDs that GH placed on routes belonging
 * to affected manifest drivers. These jobs must be dismissed when we leave
 * those manifests untouched.
 */
export const getNewJobIdsOnManifestRoutes = <T extends {driverId?: number | null; manifestDriverId?: number | null}>(
  routes: Array<{vehicle_id?: string | null; activities?: RoutePlanningActivity[] | null}>,
  affectedMdIds: Set<number>,
  submittedDrivers: T[]
): Set<number> => {
  const newJobIds = new Set<number>();
  for (const route of routes) {
    const driver = findDriverByVehicleId(route.vehicle_id, submittedDrivers);
    if (!driver?.manifestDriverId || !affectedMdIds.has(driver.manifestDriverId)) continue;

    const stopActivities = getStopActivities(route.activities);
    for (const activity of stopActivities) {
      // Only collect new job stops, not manifest stops
      const parsed = parseGhLocationId(activity.location_id);
      if (parsed?.kind === "job") {
        newJobIds.add(parsed.jobId);
      }
    }
  }
  return newJobIds;
};

export const rebuildAssignmentDetails = (
  workingRowData: RowData[],
  originalAssignmentDetails: AssignmentDetailWithEndDateTime[],
  submittedDwellSeconds: number
): AssignmentDetailWithEndDateTime[] => {
  const fallbackStartDate = originalAssignmentDetails[0]?.startDate ?? "";

  return workingRowData.map((row) => {
    // Match by manifestDriverId when present to disambiguate same-driver manifest rows.
    const original = originalAssignmentDetails.find((a) =>
      row.manifestDriverId
        ? a.submittedDriver.manifestDriverId === row.manifestDriverId
        : a.submittedDriver.driverId === row.driverId
    );
    const jobIds = [...new Set(row.assignedStops.map((s) => s.job?.jobId).filter(Boolean))] as number[];
    const sequences = row.assignedStops.map((s) => s.jobStopId);

    const optimizationEtas: EtaItemInput[] = row.assignedStops
      .filter((s) => s.eta)
      .map((s) => ({
        jobStopId: s.jobStopId,
        eta: s.eta as string,
        etaSource: s.etaSource ?? ETASourceType.ROUTE_PLANNING,
        generatedDateTime: s.generatedDateTime ?? new Date().toISOString()
      }));

    // DP-3175 B11: When a row has no manifestDriverId (new route), the matched
    // original may belong to an existing manifest with the same driverId (after
    // Change Driver). Inheriting that original's submittedDriver would carry
    // manifestDriverId, causing the backend to merge the new route's stops into
    // the existing manifest — producing duplicate sequences and blank ETAs.
    // Build a fresh submittedDriver from the row to keep it as a new manifest.
    // `row.startDateTime` is the combined plan-day instant (the submitted startTime, or the
    // one Change Driver recomputed); `row.startTime` is only the TimePicker's Dayjs, whose date
    // is the day it was picked, so it must never be serialised as the driver's start.
    const baseDriver = original?.submittedDriver ?? null;
    const submittedDriver: RoutePlanningDriverInput =
      row.manifestDriverId && baseDriver
        ? baseDriver
        : {
            driverId: row.driverId!,
            startTime: baseDriver?.startTime ?? row.startDateTime ?? null,
            startAddress: baseDriver?.startAddress ?? row.startLocation ?? null,
            startAddressPosition:
              baseDriver?.startAddressPosition ??
              (row.startLat != null && row.startLng != null
                ? {latitude: row.startLat, longitude: row.startLng}
                : undefined),
            endAddress: baseDriver?.endAddress ?? row.endLocation ?? null,
            endAddressPosition: baseDriver?.endAddressPosition,
            skillsIds: baseDriver?.skillsIds
          };

    return {
      submittedDriver,
      dwellTimeSeconds: original?.dwellTimeSeconds ?? submittedDwellSeconds,
      jobIds,
      sequences,
      startDate: original?.startDate ?? fallbackStartDate,
      optimizationEtas,
      requestEtaIfEmpty: true
    };
  });
};

const FOUR_HOURS_MS = 4 * 60 * 60 * 1000;

/**
 * DP-3202: Calculate the effective late time for a manifest stop.
 *
 * - If `lateDateTime` exists, return it directly.
 * - If `scheduledDateTime` exists and stopType is "P" (pickup), return scheduled + 4 hours.
 * - If `scheduledDateTime` exists and stopType is "D" or "B", return scheduled time.
 * - Otherwise return null (no constraint).
 */
export const calculateLateTime = (stop: ManifestStop): Date | null => {
  if (stop.lateDateTime) {
    return new Date(stop.lateDateTime);
  }
  if (stop.scheduledDateTime) {
    if (isPickupStop(stop)) {
      return new Date(new Date(stop.scheduledDateTime).getTime() + FOUR_HOURS_MS);
    }
    if (isDeliveryOrBothStop(stop)) {
      return new Date(stop.scheduledDateTime);
    }
  }
  return null;
};

/**
 * DP-3202: Identify manifests that contain at least one active stop already past
 * its late time — or whose late time falls before every driver's start time.
 *
 * When `allowMovingExistingWork` is ON, a manifest stop is flagged if no
 * selected driver can serve it in time. When OFF, only the manifest's own
 * driver is checked.
 */
export const getManifestsWithLateStops = (
  manifestStopsMap: Record<number, ManifestStop[]>,
  selectedDrivers: RoutePlanningDriver[],
  allowMovingExistingWork: boolean
): Array<{manifestDriverId: number; driverName: string}> => {
  const now = new Date();
  const result: Array<{manifestDriverId: number; driverName: string}> = [];

  for (const [manifestDriverIdStr, stops] of Object.entries(manifestStopsMap)) {
    const manifestDriverId = Number(manifestDriverIdStr);
    const manifestDriver = selectedDrivers.find((d) => d.manifestDriverId === manifestDriverId);
    if (!manifestDriver) continue;

    let isFlagged = false;

    for (const stop of stops) {
      // Only check active stops (jobStopStatus "A" or "Q")
      if (!isActiveOrQueued(stop)) continue;

      const lateTime = calculateLateTime(stop);
      if (!lateTime) continue;

      // Already past late time
      if (lateTime < now) {
        isFlagged = true;
        break;
      }

      if (allowMovingExistingWork) {
        // Check if ANY driver can serve this stop in time
        const canAnyDriverServe = selectedDrivers.some((driver) => {
          const driverStart = combineDatesAndTime(driver.startDate, driver.startTime, driver.startTimeZone);
          return driverStart.isValid() && driverStart.toDate() <= lateTime;
        });
        if (!canAnyDriverServe) {
          isFlagged = true;
          break;
        }
      } else {
        // Only check the manifest's own driver
        const ownDriverStart = combineDatesAndTime(
          manifestDriver.startDate,
          manifestDriver.startTime,
          manifestDriver.startTimeZone
        );
        if (ownDriverStart.isValid() && ownDriverStart.toDate() > lateTime) {
          isFlagged = true;
          break;
        }
      }
    }

    if (isFlagged) {
      result.push({manifestDriverId, driverName: manifestDriver.name ?? `Driver ${manifestDriver.driverCode}`});
    }
  }

  return result;
};

/**
 * DP-3219: Check whether an affected manifest's current sequence differs from
 * the initial GH-computed sequence stored in originalAssignmentDetails.
 *
 * Returns `true` when the user has modified the sequence (reordered, added, or
 * removed stops). Used by both handleCreateRoutes (to decide whether to include
 * the manifest in processRoutePlanning) and DriverResultList (to decide whether
 * a manifest counts as untouched).
 */
export const isAffectedManifestModified = (
  mdId: number,
  currentSequence: number[],
  affectedMdIds: Set<number>,
  originalAssignmentDetails: AssignmentDetailWithEndDateTime[]
): boolean => {
  if (!affectedMdIds.has(mdId)) return false;
  const initialDetail = originalAssignmentDetails.find((a) => a.submittedDriver.manifestDriverId === mdId);
  if (!initialDetail) return false;
  return (
    currentSequence.length !== initialDetail.sequences.length ||
    currentSequence.some((id, i) => id !== initialDetail.sequences[i])
  );
};

/**
 * DP-3230 (D15): the details Create Routes sends. The rebuilt ones when the dispatcher changed the plan, and also
 * when they carry skill override notes, which only travel on the rebuilt details. A manifest the plan left unchanged
 * is still dropped afterwards by handleCreateRoutes, notes included.
 */
export const selectCreateRoutesDetails = (
  modifiedDetails: {assignmentDetails: AssignmentDetailWithEndDateTime[]; isModified: boolean} | null,
  originalDetails: AssignmentDetailWithEndDateTime[]
): AssignmentDetailWithEndDateTime[] => {
  if (!modifiedDetails) return originalDetails;
  const hasSkillOverrideNotes = modifiedDetails.assignmentDetails.some((detail) => detail.skillOverrideNotes?.length);
  return modifiedDetails.isModified || hasSkillOverrideNotes ? modifiedDetails.assignmentDetails : originalDetails;
};

/**
 * DP-3219: Analyze manifest failures from GH unassigned details.
 *
 * Computes which GH-format IDs and internal job IDs should be dismissed,
 * builds filtered error details (new jobs only), and determines whether
 * any new jobs were successfully assigned.
 * An id no pattern recognises is returned in unparsedDetails, never dropped: every
 * earlier bug here was a job vanishing from a "successful" plan (DP-3237).
 */
export const analyzeManifestFailures = (
  unassignedDetails: Array<{id?: string | null; code?: number | null; reason?: string | null}>,
  solution: RoutePlanningSolution,
  submittedDrivers: Array<{driverId?: number | null; manifestDriverId?: number | null}>,
  selectedJobs: Array<{jobId: number; jobNumber?: string | null}>,
  manifestStopsMap: Record<number, ManifestStop[]>
): {
  affectedMdIds: Set<number>;
  dismissJobIds: Set<number>;
  dismissedNewJobIds: Set<number>;
  newJobOnlyDetails: Array<{id?: string | null; code?: number | null; reason?: string | null}>;
  unparsedDetails: Array<{id?: string | null; code?: number | null; reason?: string | null}>;
  hasSuccessfulNewJobs: boolean;
} => {
  // The closed set is the one every consumer needs: a manifest pulled in by a
  // cross-boundary move is frozen too, so the new jobs GH parked on it can no
  // longer be kept and must reach dismissJobIds like any other.
  const affectedMdIds = closeAffectedManifestsOverMovedStops(
    solution.routes ?? [],
    getAffectedManifestDriverIds(unassignedDetails, manifestStopsMap),
    manifestStopsMap
  );
  const newJobsOnAffected = getNewJobIdsOnManifestRoutes(solution.routes ?? [], affectedMdIds, submittedDrivers);

  const parsedDetails = unassignedDetails.map((detail) => ({detail, parsed: parseGhJobStopsId(detail.id)}));
  const unparsedDetails = parsedDetails.filter(({parsed}) => parsed === null).map(({detail}) => detail);
  for (const detail of unparsedDetails) {
    log.error(`Unassigned detail id not recognised, failing closed: ${detail.id}`);
  }
  const newJobDetails = parsedDetails.filter(({parsed}) => parsed?.kind === "job");

  // Directly-unassigned new jobs. The id carries the same jobId as selectedJobs.jobId.
  const directlyUnassignedJobIds = new Set(newJobDetails.map(({parsed}) => parsed!.jobId));

  // All dismissed GH-format IDs — used for dismissJobIds in generateGraphhopperAssignmentDetails
  const dismissJobIds = new Set([...newJobsOnAffected, ...directlyUnassignedJobIds]);

  const selectedJobIds = new Set(selectedJobs.map((j) => j.jobId));
  const dismissedNewJobIds = new Set([...dismissJobIds].filter((id) => selectedJobIds.has(id)));

  // Build error details for dismissed new jobs only — manifest jobs never shown.
  const newJobOnlyDetails: Array<{id?: string | null; code?: number | null; reason?: string | null}> =
    newJobDetails.map(({detail}) => detail);
  for (const ghId of newJobsOnAffected) {
    if (!directlyUnassignedJobIds.has(ghId)) {
      newJobOnlyDetails.push({id: `newjob:${ghId}:0`, code: 2, reason: "cannot be visited within time window"});
    }
  }

  const hasSuccessfulNewJobs = [...selectedJobIds].some((id) => !dismissedNewJobIds.has(id));

  return {affectedMdIds, dismissJobIds, dismissedNewJobIds, newJobOnlyDetails, unparsedDetails, hasSuccessfulNewJobs};
};
